import React, { useState, useEffect, useCallback, useRef } from 'react';
import './App.css';
import AdComponent from './components/AdComponent';

const BOARD_WIDTH = 10;
const BOARD_HEIGHT = 20;
const INITIAL_BOARD = Array(BOARD_HEIGHT).fill().map(() => Array(BOARD_WIDTH).fill(0));

const TETROMINOS = [
  [[1, 1, 1, 1]],
  [[1, 1], [1, 1]],
  [[1, 1, 1], [0, 1, 0]],
  [[1, 1, 1], [1, 0, 0]],
  [[1, 1, 1], [0, 0, 1]],
  [[1, 1, 0], [0, 1, 1]],
  [[0, 1, 1], [1, 1, 0]]
];

function App() {
  const [isMobile, setIsMobile] = useState(false);
  const [board, setBoard] = useState(INITIAL_BOARD);
  const [piece, setPiece] = useState(null);
  const [nextPiece, setNextPiece] = useState(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [gameOver, setGameOver] = useState(false);
  const [score, setScore] = useState(0);
  const [level, setLevel] = useState(1);
  const [isPaused, setIsPaused] = useState(false);
  const [isSoundMuted, setIsSoundMuted] = useState(false);
  const [isMusicPlaying, setIsMusicPlaying] = useState(true);
  const [fastDrop, setFastDrop] = useState(false);

  const themeAudioRef = useRef(new Audio('/sounds/theme.mp3'));
  const moveAudioRef = useRef(new Audio('/sounds/move.mp3'));
  const rotateAudioRef = useRef(new Audio('/sounds/rotate.mp3'));
  const dropAudioRef = useRef(new Audio('/sounds/drop.mp3'));
  const clearAudioRef = useRef(new Audio('/sounds/clear.mp3'));
  const lastPlayedRef = useRef({});

  const spawnPiece = useCallback(() => {
    const newPiece = nextPiece || TETROMINOS[Math.floor(Math.random() * TETROMINOS.length)];
    const nextNewPiece = TETROMINOS[Math.floor(Math.random() * TETROMINOS.length)];
    setPiece(newPiece);
    setNextPiece(nextNewPiece);
    const newX = Math.floor(BOARD_WIDTH / 2) - Math.floor(newPiece[0].length / 2);
    const newY = 0;
    setPosition({ x: newX, y: newY });
    
    if (!isValidMove(newPiece, newX, newY)) {
      setGameOver(true);
    }
  }, [nextPiece]);

  const isValidMove = (piece, x, y) => {
    for (let row = 0; row < piece.length; row++) {
      for (let col = 0; col < piece[row].length; col++) {
        if (piece[row][col]) {
          const newX = x + col;
          const newY = y + row;
          if (newX < 0 || newX >= BOARD_WIDTH || newY >= BOARD_HEIGHT || (newY >= 0 && board[newY][newX])) {
            return false;
          }
        }
      }
    }
    return true;
  };

  const playSound = useCallback((audioRef, action) => {
    if (!isSoundMuted && (!lastPlayedRef.current[action] || Date.now() - lastPlayedRef.current[action] > 50)) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(error => console.log(`${action} audio play failed:`, error));
      lastPlayedRef.current[action] = Date.now();
    }
  }, [isSoundMuted]);

  const moveDown = useCallback((isUserInitiated = false) => {
    if (!piece) return;
    if (isValidMove(piece, position.x, position.y + 1)) {
      setPosition(prev => ({ ...prev, y: prev.y + 1 }));
      if (isUserInitiated) {
        playSound(moveAudioRef, 'move');
      }
    } else {
      placePiece();
      playSound(dropAudioRef, 'drop');
    }
  }, [piece, position, playSound]);

  const moveLeft = () => {
    if (piece && isValidMove(piece, position.x - 1, position.y)) {
      setPosition(prev => ({ ...prev, x: prev.x - 1 }));
      playSound(moveAudioRef, 'move');
    }
  };

  const moveRight = () => {
    if (piece && isValidMove(piece, position.x + 1, position.y)) {
      setPosition(prev => ({ ...prev, x: prev.x + 1 }));
      playSound(moveAudioRef, 'move');
    }
  };

  const rotate = () => {
    if (!piece) return;
    const rotatedPiece = piece[0].map((_, index) => piece.map(row => row[index]).reverse());
    if (isValidMove(rotatedPiece, position.x, position.y)) {
      setPiece(rotatedPiece);
      playSound(rotateAudioRef, 'rotate');
    }
  };

  const startFastDrop = () => {
    setFastDrop(true);
  };

  const endFastDrop = () => {
    setFastDrop(false);
  };

  const placePiece = () => {
    const newBoard = board.map(row => [...row]);
    for (let row = 0; row < piece.length; row++) {
      for (let col = 0; col < piece[row].length; col++) {
        if (piece[row][col]) {
          const newY = position.y + row;
          const newX = position.x + col;
          if (newY < 0) {
            setGameOver(true);
            return;
          }
          newBoard[newY][newX] = 1;
        }
      }
    }
    setBoard(newBoard);
    clearLines(newBoard);
    spawnPiece();
  };

  const clearLines = useCallback((board) => {
    const newBoard = board.filter(row => row.some(cell => cell === 0));
    const clearedLines = BOARD_HEIGHT - newBoard.length;
    const newRows = Array(clearedLines).fill().map(() => Array(BOARD_WIDTH).fill(0));
    setBoard([...newRows, ...newBoard]);

    if (clearedLines > 0) {
      const points = clearedLines === 1 ? 10 : 
                     clearedLines === 2 ? 20 : 
                     clearedLines === 3 ? 30 : 
                     clearedLines === 4 ? 50 : 0;
      setScore(prevScore => {
        const newScore = prevScore + points;
        if (Math.floor(newScore / 200) > Math.floor(prevScore / 200)) {
          setLevel(prevLevel => prevLevel + 1);
        }
        return newScore;
      });
      playSound(clearAudioRef, 'clear');
    }
  }, [playSound, setLevel]);

  const toggleMute = () => {
    setIsSoundMuted(prev => !prev);
  };

  const toggleMusic = useCallback(() => {
    setIsMusicPlaying(prevPlaying => {
      if (prevPlaying) {
        themeAudioRef.current.pause();
      } else {
        themeAudioRef.current.play().catch(error => console.log("Theme audio play failed:", error));
      }
      return !prevPlaying;
    });
  }, []);

  const togglePause = () => {
    setIsPaused(prev => !prev);
  };

  const resetGame = useCallback(() => {
    setBoard(INITIAL_BOARD);
    setPiece(null);
    setNextPiece(null);
    setPosition({ x: 0, y: 0 });
    setGameOver(false);
    setScore(0);
    setLevel(1);
    setIsPaused(false);
    setFastDrop(false);
    
    // Force a re-render and then spawn a new piece
    setTimeout(() => {
      spawnPiece();
    }, 0);
  }, [spawnPiece]);

  const calculateDropTime = useCallback(() => {
    return Math.max(100, 1000 - (level - 1) * 100);
  }, [level]);

  useEffect(() => {
    themeAudioRef.current.loop = true;
    themeAudioRef.current.volume = 0.5;
    return () => {
      themeAudioRef.current.pause();
    };
  }, []);

  useEffect(() => {
    if (!isSoundMuted && isMusicPlaying) {
      themeAudioRef.current.play().catch(error => console.log("Theme audio play failed:", error));
    } else {
      themeAudioRef.current.pause();
    }
  }, [isSoundMuted, isMusicPlaying]);

  useEffect(() => {
    if (gameOver || isPaused) return;
    
    const gameLoop = () => {
      moveDown(false);
    };

    const intervalId = setInterval(gameLoop, fastDrop ? 50 : calculateDropTime());

    return () => {
      clearInterval(intervalId);
    };
  }, [gameOver, isPaused, level, moveDown, calculateDropTime, fastDrop]);

  useEffect(() => {
    if (!piece) spawnPiece();
  }, [piece, spawnPiece]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (gameOver || isPaused) return;
      e.preventDefault();
      switch (e.keyCode) {
        case 37: moveLeft(); break;
        case 39: moveRight(); break;
        case 40: moveDown(true); break;
        case 38: rotate(); break;
        case 32: startFastDrop(); break;
        default: break;
      }
    };

    const handleKeyUp = (e) => {
      if (gameOver || isPaused) return;
      if (e.keyCode === 32) {
        e.preventDefault();
        endFastDrop();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, [gameOver, isPaused, moveDown]);

  useEffect(() => {
    const checkDevice = () => {
      const mobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobile(mobile);
    };

    checkDevice();
    window.addEventListener('resize', checkDevice);

    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  if (isMobile) {
    return (
      <div className="mobile-message">
        <h1>Taitris</h1>
        <p>Apologies for the inconvenience, but Taitris is currently only available as a PC desktop web app.</p>
        <p>Please access this app from a web browser on your PC!</p>
      </div>
    );
  }

  const renderBoard = () => {
    const displayBoard = board.map(row => [...row]);
    if (piece) {
      for (let row = 0; row < piece.length; row++) {
        for (let col = 0; col < piece[row].length; col++) {
          if (piece[row][col]) {
            const newY = position.y + row;
            const newX = position.x + col;
            if (newY >= 0 && newY < BOARD_HEIGHT && newX >= 0 && newX < BOARD_WIDTH) {
              displayBoard[newY][newX] = 1;
            }
          }
        }
      }
    }
    return displayBoard;
  };

  return (
    <div className="App">
      <div className="game-container">
        <div className="title">
          <span>T</span><span>A</span><span>I</span><span>T</span><span>R</span><span>I</span><span>S</span>
        </div>
        <div className="game-area">
          <div className="left-panel">
            <div className="score-box">
              <h2>Score</h2>
              <p>{score}</p>
            </div>
            <div className="level-box">
              <h2>Level</h2>
              <p>{level}</p>
            </div>
            <div className="controls">
              <button onClick={toggleMute}>
                {isSoundMuted ? 'Unmute Sound' : 'Mute Sound'}
              </button>
              <button onClick={toggleMusic}>
                {isMusicPlaying ? 'Pause Music' : 'Play Music'}
              </button>
              <button onClick={togglePause}>
                {isPaused ? 'Resume Game' : 'Pause Game'}
              </button>
            </div>
          </div>
          <div className="board">
            {renderBoard().map((row, rowIndex) => (
              <div key={rowIndex} className="row">
                {row.map((cell, cellIndex) => (
                  <div
                    key={cellIndex}
                    className={`cell ${cell ? 'filled' : ''}`}
                  />
                ))}
              </div>
            ))}
            {gameOver && (
              <div className="game-over">
                <p>Game Over</p>
                <button onClick={resetGame}>New Game</button>
              </div>
            )}
          </div>
          <div className="right-panel">
            <div className="next-piece-box">
              <h2>Next Piece</h2>
              <div className="next-piece">
                {nextPiece && (
                  <div className="next-piece-grid">
                    {nextPiece.map((row, rowIndex) => (
                      <div key={rowIndex} className="row">
                        {row.map((cell, cellIndex) => (
                          <div
                            key={cellIndex}
                            className={`cell ${cell ? 'filled' : ''}`}
                          />
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {!isMobile && <AdComponent />}
        <div className="credits">
          by <a href="https://sachadjordjevic.com" target="_blank" rel="noopener noreferrer">Sacha Djordjevic</a> | <a href="https://11000ad.com" target="_blank" rel="noopener noreferrer">11000AD.com</a>
        </div>
      </div>
    </div>
  );
}

export default App;
